import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  Truck,
  Plus,
  Search,
  Edit2,
  Eye,
  FileText,
  RefreshCw,
  DollarSign,
  AlertCircle,
  CheckCircle2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Badge } from "../../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

const CONDICIONES_IVA = [
  { value: "RI", label: "Responsable Inscripto" },
  { value: "MT", label: "Monotributo" },
  { value: "EX", label: "Exento" },
  { value: "CF", label: "Consumidor Final" },
  { value: "NR", label: "No Responsable" },
];

const CuentasCorrientes = () => {
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [activeTab, setActiveTab] = useState("clientes");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modal states
  const [modalCliente, setModalCliente] = useState(false);
  const [modalProveedor, setModalProveedor] = useState(false);
  const [modalEstadoCuenta, setModalEstadoCuenta] = useState(false);
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form data
  const [formCliente, setFormCliente] = useState({
    codigo: "",
    razon_social: "",
    cuit: "",
    condicion_iva: "CF",
    email: "",
    telefono: "",
    direccion: "",
    localidad: "",
    provincia: ""
  });
  
  const [formProveedor, setFormProveedor] = useState({
    codigo: "",
    razon_social: "",
    cuit: "",
    condicion_iva: "RI",
    email: "",
    telefono: "",
    direccion: "",
    cbu: "",
    banco: ""
  });

  const fetchClientes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/clientes`);
      setClientes(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchProveedores = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/proveedores`);
      setProveedores(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchClientes(), fetchProveedores()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleOpenModalCliente = (cliente = null) => {
    if (cliente) {
      setEditMode(true);
      setSelectedItem(cliente);
      setFormCliente({
        codigo: cliente.codigo,
        razon_social: cliente.razon_social,
        cuit: cliente.cuit || "",
        condicion_iva: cliente.condicion_iva || "CF",
        email: cliente.email || "",
        telefono: cliente.telefono || "",
        direccion: cliente.direccion || "",
        localidad: cliente.localidad || "",
        provincia: cliente.provincia || ""
      });
    } else {
      setEditMode(false);
      setSelectedItem(null);
      setFormCliente({
        codigo: "",
        razon_social: "",
        cuit: "",
        condicion_iva: "CF",
        email: "",
        telefono: "",
        direccion: "",
        localidad: "",
        provincia: ""
      });
    }
    setModalCliente(true);
  };

  const handleOpenModalProveedor = (proveedor = null) => {
    if (proveedor) {
      setEditMode(true);
      setSelectedItem(proveedor);
      setFormProveedor({
        codigo: proveedor.codigo,
        razon_social: proveedor.razon_social,
        cuit: proveedor.cuit || "",
        condicion_iva: proveedor.condicion_iva || "RI",
        email: proveedor.email || "",
        telefono: proveedor.telefono || "",
        direccion: proveedor.direccion || "",
        cbu: proveedor.cbu || "",
        banco: proveedor.banco || ""
      });
    } else {
      setEditMode(false);
      setSelectedItem(null);
      setFormProveedor({
        codigo: "",
        razon_social: "",
        cuit: "",
        condicion_iva: "RI",
        email: "",
        telefono: "",
        direccion: "",
        cbu: "",
        banco: ""
      });
    }
    setModalProveedor(true);
  };

  const handleSaveCliente = async () => {
    if (!formCliente.codigo || !formCliente.razon_social) {
      toast.error("Complete los campos requeridos");
      return;
    }
    setSaving(true);
    try {
      if (editMode) {
        await axios.put(`${API_URL}/api/erp/empresas/${TENANT_ID}/clientes/${selectedItem.id}`, formCliente);
        toast.success("Cliente actualizado");
      } else {
        await axios.post(`${API_URL}/api/erp/empresas/${TENANT_ID}/clientes`, formCliente);
        toast.success("Cliente creado");
      }
      setModalCliente(false);
      fetchClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProveedor = async () => {
    if (!formProveedor.codigo || !formProveedor.razon_social) {
      toast.error("Complete los campos requeridos");
      return;
    }
    setSaving(true);
    try {
      if (editMode) {
        await axios.put(`${API_URL}/api/erp/empresas/${TENANT_ID}/proveedores/${selectedItem.id}`, formProveedor);
        toast.success("Proveedor actualizado");
      } else {
        await axios.post(`${API_URL}/api/erp/empresas/${TENANT_ID}/proveedores`, formProveedor);
        toast.success("Proveedor creado");
      }
      setModalProveedor(false);
      fetchProveedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleVerEstadoCuenta = async (tipo, id) => {
    try {
      const endpoint = tipo === "cliente" 
        ? `${API_URL}/api/erp/empresas/${TENANT_ID}/clientes/${id}/estado-cuenta`
        : `${API_URL}/api/erp/empresas/${TENANT_ID}/proveedores/${id}/estado-cuenta`;
      const response = await axios.get(endpoint);
      setEstadoCuenta(response.data);
      setModalEstadoCuenta(true);
    } catch (error) {
      toast.error("Error al cargar estado de cuenta");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(value);
  };

  const clientesFiltrados = clientes.filter(c => 
    c.razon_social.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.codigo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const proveedoresFiltrados = proveedores.filter(p => 
    p.razon_social.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.codigo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalDeudaClientes = clientes.reduce((sum, c) => sum + parseFloat(c.saldo_cuenta_corriente || 0), 0);
  const totalDeudaProveedores = proveedores.reduce((sum, p) => sum + parseFloat(p.saldo_cuenta_corriente || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="erp-cuentas-corrientes">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Cuentas Corrientes</h1>
          <p className="text-gray-600">Gestion de clientes y proveedores</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="cao-card border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Deuda Clientes (A Cobrar)</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(totalDeudaClientes)}</p>
                <p className="text-xs text-gray-400">{clientes.length} clientes</p>
              </div>
              <Users className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="cao-card border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Deuda Proveedores (A Pagar)</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(totalDeudaProveedores)}</p>
                <p className="text-xs text-gray-400">{proveedores.length} proveedores</p>
              </div>
              <Truck className="w-10 h-10 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border shadow-sm">
          <TabsTrigger value="clientes" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-2" />
            Clientes
          </TabsTrigger>
          <TabsTrigger value="proveedores" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <Truck className="w-4 h-4 mr-2" />
            Proveedores
          </TabsTrigger>
        </TabsList>

        {/* Clientes Tab */}
        <TabsContent value="clientes" className="mt-6">
          <Card className="cao-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-4">
                <CardTitle className="text-[#1E3876]">Clientes</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
              <Button onClick={() => handleOpenModalCliente()} data-testid="btn-nuevo-cliente">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Cliente
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Codigo</TableHead>
                    <TableHead>Razon Social</TableHead>
                    <TableHead>CUIT</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Telefono</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientesFiltrados.map((cliente) => (
                    <TableRow key={cliente.id}>
                      <TableCell className="font-mono">{cliente.codigo}</TableCell>
                      <TableCell className="font-medium">{cliente.razon_social}</TableCell>
                      <TableCell>{cliente.cuit || "-"}</TableCell>
                      <TableCell>{cliente.email || "-"}</TableCell>
                      <TableCell>{cliente.telefono || "-"}</TableCell>
                      <TableCell className={`text-right font-bold ${parseFloat(cliente.saldo_cuenta_corriente) > 0 ? "text-green-600" : ""}`}>
                        {formatCurrency(cliente.saldo_cuenta_corriente)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleVerEstadoCuenta("cliente", cliente.id)} title="Ver Estado de Cuenta">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleOpenModalCliente(cliente)} title="Editar">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Proveedores Tab */}
        <TabsContent value="proveedores" className="mt-6">
          <Card className="cao-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-4">
                <CardTitle className="text-[#1E3876]">Proveedores</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
              <Button onClick={() => handleOpenModalProveedor()} data-testid="btn-nuevo-proveedor">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Proveedor
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Codigo</TableHead>
                    <TableHead>Razon Social</TableHead>
                    <TableHead>CUIT</TableHead>
                    <TableHead>Cond. IVA</TableHead>
                    <TableHead>CBU</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {proveedoresFiltrados.map((prov) => (
                    <TableRow key={prov.id}>
                      <TableCell className="font-mono">{prov.codigo}</TableCell>
                      <TableCell className="font-medium">{prov.razon_social}</TableCell>
                      <TableCell>{prov.cuit || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{prov.condicion_iva}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{prov.cbu || "-"}</TableCell>
                      <TableCell className={`text-right font-bold ${parseFloat(prov.saldo_cuenta_corriente) > 0 ? "text-red-600" : ""}`}>
                        {formatCurrency(prov.saldo_cuenta_corriente)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleVerEstadoCuenta("proveedor", prov.id)} title="Ver Estado de Cuenta">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleOpenModalProveedor(prov)} title="Editar">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Cliente */}
      <Dialog open={modalCliente} onOpenChange={setModalCliente}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-[#1E3876]">{editMode ? "Editar Cliente" : "Nuevo Cliente"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Codigo *</Label>
                <Input value={formCliente.codigo} onChange={(e) => setFormCliente({ ...formCliente, codigo: e.target.value })} placeholder="CLI-001" />
              </div>
              <div>
                <Label>CUIT</Label>
                <Input value={formCliente.cuit} onChange={(e) => setFormCliente({ ...formCliente, cuit: e.target.value })} placeholder="20-12345678-9" />
              </div>
            </div>
            <div>
              <Label>Razon Social *</Label>
              <Input value={formCliente.razon_social} onChange={(e) => setFormCliente({ ...formCliente, razon_social: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Condicion IVA</Label>
                <Select value={formCliente.condicion_iva} onValueChange={(v) => setFormCliente({ ...formCliente, condicion_iva: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDICIONES_IVA.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Telefono</Label>
                <Input value={formCliente.telefono} onChange={(e) => setFormCliente({ ...formCliente, telefono: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={formCliente.email} onChange={(e) => setFormCliente({ ...formCliente, email: e.target.value })} />
            </div>
            <div>
              <Label>Direccion</Label>
              <Input value={formCliente.direccion} onChange={(e) => setFormCliente({ ...formCliente, direccion: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Localidad</Label>
                <Input value={formCliente.localidad} onChange={(e) => setFormCliente({ ...formCliente, localidad: e.target.value })} />
              </div>
              <div>
                <Label>Provincia</Label>
                <Input value={formCliente.provincia} onChange={(e) => setFormCliente({ ...formCliente, provincia: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalCliente(false)}>Cancelar</Button>
            <Button onClick={handleSaveCliente} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Proveedor */}
      <Dialog open={modalProveedor} onOpenChange={setModalProveedor}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-[#1E3876]">{editMode ? "Editar Proveedor" : "Nuevo Proveedor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Codigo *</Label>
                <Input value={formProveedor.codigo} onChange={(e) => setFormProveedor({ ...formProveedor, codigo: e.target.value })} placeholder="PROV-001" />
              </div>
              <div>
                <Label>CUIT</Label>
                <Input value={formProveedor.cuit} onChange={(e) => setFormProveedor({ ...formProveedor, cuit: e.target.value })} placeholder="30-12345678-9" />
              </div>
            </div>
            <div>
              <Label>Razon Social *</Label>
              <Input value={formProveedor.razon_social} onChange={(e) => setFormProveedor({ ...formProveedor, razon_social: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Condicion IVA</Label>
                <Select value={formProveedor.condicion_iva} onValueChange={(v) => setFormProveedor({ ...formProveedor, condicion_iva: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDICIONES_IVA.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Telefono</Label>
                <Input value={formProveedor.telefono} onChange={(e) => setFormProveedor({ ...formProveedor, telefono: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={formProveedor.email} onChange={(e) => setFormProveedor({ ...formProveedor, email: e.target.value })} />
            </div>
            <div>
              <Label>Direccion</Label>
              <Input value={formProveedor.direccion} onChange={(e) => setFormProveedor({ ...formProveedor, direccion: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CBU</Label>
                <Input value={formProveedor.cbu} onChange={(e) => setFormProveedor({ ...formProveedor, cbu: e.target.value })} maxLength={22} />
              </div>
              <div>
                <Label>Banco</Label>
                <Input value={formProveedor.banco} onChange={(e) => setFormProveedor({ ...formProveedor, banco: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalProveedor(false)}>Cancelar</Button>
            <Button onClick={handleSaveProveedor} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Estado de Cuenta */}
      <Dialog open={modalEstadoCuenta} onOpenChange={setModalEstadoCuenta}>
        <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1E3876]">
              Estado de Cuenta - {estadoCuenta?.entidad_nombre}
            </DialogTitle>
          </DialogHeader>
          {estadoCuenta && (
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Estado</p>
                  <Badge className={
                    estadoCuenta.estado === "AL_DIA" ? "bg-green-100 text-green-700" :
                    estadoCuenta.estado === "DEUDOR" ? "bg-red-100 text-red-700" :
                    "bg-blue-100 text-blue-700"
                  }>
                    {estadoCuenta.estado === "AL_DIA" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                    {estadoCuenta.estado === "DEUDOR" && <AlertCircle className="w-3 h-3 mr-1" />}
                    {estadoCuenta.estado.replace("_", " ")}
                  </Badge>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Saldo Actual</p>
                  <p className={`text-2xl font-bold ${estadoCuenta.saldo_actual > 0 ? "text-red-600" : estadoCuenta.saldo_actual < 0 ? "text-green-600" : ""}`}>
                    {formatCurrency(Math.abs(estadoCuenta.saldo_actual))}
                  </p>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Comprobante</TableHead>
                    <TableHead>Concepto</TableHead>
                    <TableHead className="text-right">Debe</TableHead>
                    <TableHead className="text-right">Haber</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {estadoCuenta.movimientos.map((mov, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{new Date(mov.fecha).toLocaleDateString("es-AR")}</TableCell>
                      <TableCell className="font-mono text-sm">{mov.tipo_comprobante} {mov.numero_comprobante}</TableCell>
                      <TableCell>{mov.concepto || "-"}</TableCell>
                      <TableCell className="text-right">{mov.debe > 0 ? formatCurrency(mov.debe) : ""}</TableCell>
                      <TableCell className="text-right">{mov.haber > 0 ? formatCurrency(mov.haber) : ""}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(mov.saldo)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalEstadoCuenta(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CuentasCorrientes;
